import { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { criarRecebimento } from '@/integrations/supabase/recebimentos-pedidos';
import { Loader2, Plus, Trash2, CheckCircle2 } from 'lucide-react';

interface RecebimentoItem {
  id: string;
  pedidoId: string;
  valor: string;
  dataRecebimento: string;
  formaPagamento: string;
}

interface DialogRegistrarRecebimentosMultiplosProps {
  isOpen: boolean;
  onClose: () => void;
  onRecebimentosCriados?: () => void;
}

export function DialogRegistrarRecebimentosMultiplos({
  isOpen,
  onClose,
  onRecebimentosCriados
}: DialogRegistrarRecebimentosMultiplosProps) {
  const [recebimentos, setRecebimentos] = useState<RecebimentoItem[]>([
    {
      id: '1',
      pedidoId: '',
      valor: '',
      dataRecebimento: new Date().toISOString().split('T')[0],
      formaPagamento: ''
    }
  ]);
  const [isLoading, setIsLoading] = useState(false);

  const adicionarLinhaRecebimento = () => {
    const novoId = (Math.max(...recebimentos.map(r => parseInt(r.id) || 0), 0) + 1).toString();
    setRecebimentos([...recebimentos, {
      id: novoId,
      pedidoId: '',
      valor: '',
      dataRecebimento: new Date().toISOString().split('T')[0],
      formaPagamento: ''
    }]);
  };

  const removerLinhaRecebimento = (id: string) => {
    if (recebimentos.length > 1) {
      setRecebimentos(recebimentos.filter(r => r.id !== id));
    } else {
      toast.error('Deve haver pelo menos um recebimento');
    }
  };

  const atualizarRecebimento = (id: string, campo: keyof RecebimentoItem, valor: string) => {
    setRecebimentos(recebimentos.map(r =>
      r.id === id ? { ...r, [campo]: valor } : r
    ));
  };

  const handleSubmit = async () => {
    // Validar
    for (const recebimento of recebimentos) {
      if (!recebimento.pedidoId.trim()) {
        toast.error('Todos os Pedidos ID são obrigatórios');
        return;
      }
      if (!recebimento.valor || parseFloat(recebimento.valor) <= 0) {
        toast.error('Todos os valores devem ser maiores que zero');
        return;
      }
      if (!recebimento.dataRecebimento) {
        toast.error('Todas as datas são obrigatórias');
        return;
      }
    }

    setIsLoading(true);
    let sucessos = 0;
    let erros = 0;

    try {
      for (const recebimento of recebimentos) {
        try {
          const result = await criarRecebimento({
            order_id: recebimento.pedidoId,
            valor_recebido: parseFloat(recebimento.valor),
            data_recebimento: recebimento.dataRecebimento,
            forma_pagamento: recebimento.formaPagamento || undefined
          });

          if (result.ok) {
            sucessos++;
          } else {
            erros++;
          }
        } catch (erro) {
          erros++;
        }
      }

      if (sucessos > 0 && erros === 0) {
        toast.success(`✅ ${sucessos} recebimento(s) registrado(s) com sucesso!`);
        setRecebimentos([{
          id: '1',
          pedidoId: '',
          valor: '',
          dataRecebimento: new Date().toISOString().split('T')[0],
          formaPagamento: ''
        }]);
        onRecebimentosCriados?.();
        onClose();
      } else if (sucessos > 0 && erros > 0) {
        toast.warning(`⚠️ ${sucessos} registrado(s), ${erros} erro(s)`);
      } else {
        toast.error('❌ Erro ao registrar recebimentos');
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-96 overflow-y-auto border-purple-200">
        <DialogHeader>
          <DialogTitle className="text-purple-900 text-xl flex items-center gap-2">
            <CheckCircle2 className="w-5 h-5 text-purple-600" />
            Registrar Múltiplos Recebimentos
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <div className="bg-purple-50 p-3 rounded-lg text-sm text-purple-700">
            ℹ️ Registre vários recebimentos de uma vez - preencha os dados e clique em Salvar
          </div>

          <div className="space-y-3 max-h-64 overflow-y-auto">
            {recebimentos.map((recebimento, idx) => (
              <div key={recebimento.id} className="p-3 bg-white border border-purple-100 rounded-lg space-y-2">
                <div className="flex items-center justify-between">
                  <p className="text-xs font-semibold text-gray-600">Recebimento {idx + 1}</p>
                  {recebimentos.length > 1 && (
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => removerLinhaRecebimento(recebimento.id)}
                      className="h-7 w-7 text-red-600 hover:bg-red-50"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  )}
                </div>

                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <Label htmlFor={`pedido-${recebimento.id}`} className="text-xs font-medium">Pedido ID</Label>
                    <Input
                      id={`pedido-${recebimento.id}`}
                      placeholder="ID do pedido"
                      value={recebimento.pedidoId}
                      onChange={(e) => atualizarRecebimento(recebimento.id, 'pedidoId', e.target.value)}
                      className="h-8 text-sm border-purple-200"
                    />
                  </div>
                  <div>
                    <Label htmlFor={`valor-${recebimento.id}`} className="text-xs font-medium">Valor (R$)</Label>
                    <Input
                      id={`valor-${recebimento.id}`}
                      type="number"
                      step="0.01"
                      min="0"
                      placeholder="0,00"
                      value={recebimento.valor}
                      onChange={(e) => atualizarRecebimento(recebimento.id, 'valor', e.target.value)}
                      className="h-8 text-sm border-purple-200"
                    />
                  </div>
                  <div>
                    <Label htmlFor={`data-${recebimento.id}`} className="text-xs font-medium">Data</Label>
                    <Input
                      id={`data-${recebimento.id}`}
                      type="date"
                      value={recebimento.dataRecebimento}
                      onChange={(e) => atualizarRecebimento(recebimento.id, 'dataRecebimento', e.target.value)}
                      className="h-8 text-sm border-purple-200"
                    />
                  </div>
                  <div>
                    <Label htmlFor={`forma-${recebimento.id}`} className="text-xs font-medium">Forma</Label>
                    <Select value={recebimento.formaPagamento} onValueChange={(v) => atualizarRecebimento(recebimento.id, 'formaPagamento', v)}>
                      <SelectTrigger id={`forma-${recebimento.id}`} className="h-8 text-sm border-purple-200">
                        <SelectValue placeholder="Selecione" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="pix">PIX</SelectItem>
                        <SelectItem value="dinheiro">Dinheiro</SelectItem>
                        <SelectItem value="cartao-debito">Cartão Débito</SelectItem>
                        <SelectItem value="cartao-credito">Cartão Crédito</SelectItem>
                        <SelectItem value="transferencia">Transferência</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <Button
            variant="outline"
            size="sm"
            onClick={adicionarLinhaRecebimento}
            className="w-full border-purple-200 text-purple-600 hover:bg-purple-50"
          >
            <Plus className="w-4 h-4 mr-2" />
            Adicionar Recebimento
          </Button>
        </div>

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={onClose} disabled={isLoading} className="border-purple-200">
            Cancelar
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={isLoading}
            className="bg-gradient-to-r from-purple-600 to-purple-700 hover:from-purple-700 hover:to-purple-800 text-white"
          >
            {isLoading ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Salvando...
              </>
            ) : (
              <>
                <CheckCircle2 className="w-4 h-4 mr-2" />
                Salvar Recebimentos
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
